<template>
  <div>
    <div v-if="hike">
      <div class="d-flex justify-content-between">
      
          <button @click="addComment" class="btn btn-secondary">
            {{ commentButtonLabel }}
          </button>
        
          <span class="d-flex justify-content-end p-3">
            <select class="custom-select rounded-pill" v-model="selectedOrder">
              <option v-for="order in orderings" :key="order" :value="order" >{{ $t('comments.sort_by_order', { order: orderLabel(order) }) }}</option>
            </select>
          </span>
        
      </div>

      <div class="hike-comments">
        <div id="comments_container">
          <CommentThread 
            v-for="comment in orderedComments"
            :key="comment.id"
            :comment="comment"
            :depth="0"
            :max-depth="4"
            :current-user="user"
            @reply-posted="handleReplyPosted"
            @comment-updated="handleCommentUpdated"
            @comment-deleted="handleCommentDeleted"
          />
        </div>
        <div v-if="hasMoreComments" class="text-center mt-4">
          <button
            class="btn btn-outline-secondary"
            type="button"
            :disabled="loadingMoreComments"
            @click="loadMoreComments"
          >
            <span v-if="loadingMoreComments" class="spinner-border spinner-border-sm mr-2"></span>
            {{ loadingMoreComments ? $t('shared.loading') : $t('comments.load_more_comments') }}
          </button>
        </div>
      </div>
    </div>
    <p v-if="hike === null" class="d-flex align-items-center my-3 display-5">
      <span class="spinner-border spinner-border-md mr-2"></span>
      {{ $t('comments.loading_comments') }}
    </p>
    <div v-if="error">{{ error }}</div>
    <div v-if="hike && hike.comments && hike.comments.length === 0 && !showModal">
      <div class="mt-3">
        <h4><strong>{{ $t('comments.no_comments_yet') }}</strong></h4>
        <p>{{ noCommentsMessage }}</p>
      </div>
    </div>
    <CommentDialog ref="commentDialog" />
    <LoadingDialog :show-loading-dialog="newCommentLoading"/>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { provide, inject, getCurrentInstance } from 'vue';
import CommentThread from './CommentThread.vue';
import CommentDialog from './CommentDialog.vue';
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import graph_client from '../../utils/graphql/graph_client';

export default {
  components: {
    LoadingDialog,
    CommentThread,
    CommentDialog,
  },
  setup() {
    // Provide GraphQL client to child components
    provide('graphClient', graph_client);
    // Provide store for child components  
    const instance = getCurrentInstance();
    if (instance) {
      provide('store', instance.proxy.$store);
    }
  },
  computed: {
    ...mapState(['hike', 'user', 'error', 'orderings', 'showModal', 'newCommentLoading', 'loadingMoreComments', 'commentsPagination']),
    ...mapGetters(['orderedComments']),
    selectedOrder: {
      get() {
        return this.$store.state.selectedOrder;
      },
      set(value) {
        this.$store.dispatch('setSelectedOrder', value);
      },
    },
    commentableName() {
      return this.$store.state.commentableType === 'Recording' ? 'recording' : 'hike';
    },
    commentButtonLabel() {
      return this.commentableName === 'recording'
        ? this.$t('comments.comment_on_this_recording')
        : this.$t('comments.comment_on_this_hike');
    },
    noCommentsMessage() {
      return this.commentableName === 'recording'
        ? this.$t('comments.be_first_to_comment_recording')
        : this.$t('comments.be_first_to_comment_hike');
    },
    hasMoreComments() {
      return this.commentsPagination?.hasNextPage;
    }
  },
  methods: {
    addComment() {
      this.$store.dispatch('addComment', null);
    },
    
    async handleReplyPosted({ parentCommentId, body }) {
      console.log('[App.vue] handleReplyPosted received:', { parentCommentId, body });
      console.log('[App.vue] parentCommentId type:', typeof parentCommentId);
      // Handle reply directly without showing modal
      try {
        await this.$store.dispatch('submitReply', { parentCommentId, body });
      } catch (error) {
        console.error('Error posting reply:', error);
        alert(this.$t('comments.failed_to_post_reply'));
      }
    },
    
    handleCommentUpdated(commentId) {
      // Handle comment update - for now just refresh
      console.log('Comment updated:', commentId);
      this.$store.dispatch('fetchHike');
    },
    
    handleCommentDeleted(commentId) {
      // Find the comment and delete it
      const findComment = (comments) => {
        for (const comment of comments) {
          if (comment.id === commentId) return comment;
          if (comment.childComments) {
            const found = findComment(comment.childComments);
            if (found) return found;
          }
        }
        return null;
      };
      
      if (this.hike && this.hike.comments) {
        const comment = findComment(this.hike.comments);
        if (comment) {
          this.$store.dispatch('destroyComment', comment);
        }
      }
    },

    loadMoreComments() {
      this.$store.dispatch('loadMoreComments');
    },

    orderLabel(order) {
      switch (order) {
        case 'created': return this.$t('comments.order_created');
        case 'rating': return this.$t('comments.order_rating');
        case 'ranking': return this.$t('comments.order_ranking');
        default: return order;
      }
    }
  },
  mounted() {
    this.$store.dispatch('fetchHike');
  },
};
</script>

<style scoped>
  .custom-select{
    background: transparent;
  }
</style>
